import { useEffect, useState } from "react";
import { submitContact } from "../lib/api";
import { CHILDREN_UNITED_HOPE } from "../data/kids";
import SEO from "../components/SEO";

export default function Contact() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [form, setForm] = useState({ name: "", email: "", subject: "General Inquiry", message: "" });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setStatus(null);
    try {
      await submitContact(form);
      setStatus("Thank you — your message is in. We'll be in touch.");
      setForm({ name: "", email: "", subject: "General Inquiry", message: "" });
    } catch {
      setStatus("Something went wrong. Please email us directly at fostercareawareness365@gmail.com.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        path="/contact"
        title="Contact Foster Care Awareness 365 | Reach Out & Connect"
        description="Get in touch with Foster Care Awareness 365. Whether you're a foster family, advocate, organization, or media — we want to hear from you."
      />
      <section className="relative overflow-hidden border-b border-fc-border bg-fc-ink">
        {/* Cinematic background — FULL color, no white-wash */}
        <div
          className="absolute inset-0 ken-burns"
          style={{
            backgroundImage: `url(${CHILDREN_UNITED_HOPE})`,
            backgroundSize: "cover",
            backgroundPosition: "center 35%",
          }}
          aria-hidden="true"
        />
        {/* Dark scrim — left-heavy so all hero copy reads white */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/25" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-black/70" aria-hidden="true" />

        <div className="container-xl relative pt-20 pb-20 md:pt-28 md:pb-28">
          <div className="overline text-fc-accent mb-5">Contact · We Read Every Message</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl text-white">
            Start a conversation. <span className="italic text-fc-accent">We read every message.</span>
          </h1>
          <p className="mt-6 text-white max-w-2xl text-base md:text-lg leading-relaxed">
            Partnerships, speaking engagements, music licensing, foster-care advocacy programs, or press — we'd love to hear what's on your mind.
          </p>
        </div>
      </section>

      <section className="container-xl py-20 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5 space-y-6 text-fc-muted leading-relaxed">
          <p>
            Whether you're an advocacy organization, a foster family, a church, or a case worker — we'd love to hear from you. Reach out about partnerships, speaking, or just to share what the music has meant to your community.
          </p>
          <div className="pt-6 border-t border-fc-border space-y-4">
            <div>
              <div className="overline text-fc-muted mb-1">Email</div>
              <a href="mailto:fostercareawareness365@gmail.com" className="font-mono text-fc-ink link-ul" data-testid="contact-email">fostercareawareness365@gmail.com</a>
            </div>
            <div>
              <div className="overline text-fc-muted mb-1">Licensing</div>
              <a href="/anthem/licensing" className="font-mono text-fc-ink link-ul" data-testid="contact-licensing-link">Submit a licensing inquiry →</a>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="md:col-span-7 space-y-8" data-testid="contact-form">
          <div>
            <label className="fc-label block mb-2">Your Name</label>
            <input required value={form.name} onChange={update("name")} className="fc-input" placeholder="Full name" data-testid="contact-name" />
          </div>
          <div>
            <label className="fc-label block mb-2">Email</label>
            <input required type="email" value={form.email} onChange={update("email")} className="fc-input" placeholder="you@example.com" data-testid="contact-email-input" />
          </div>
          <div>
            <label className="fc-label block mb-2">Subject</label>
            <input value={form.subject} onChange={update("subject")} className="fc-input" placeholder="General Inquiry" data-testid="contact-subject" />
          </div>
          <div>
            <label className="fc-label block mb-2">Message</label>
            <textarea required rows={6} value={form.message} onChange={update("message")} className="fc-input resize-none pt-3" placeholder="Tell us about your organization, event, or story." data-testid="contact-message" />
          </div>
          <button className="btn-primary" disabled={busy} data-testid="contact-submit">
            {busy ? "Sending…" : "Send Message"}
          </button>
          {status && <div className="overline pt-2" data-testid="contact-status" style={{color:"var(--fc-accent)"}}>{status}</div>}
        </form>
      </section>
    </div>
  );
}
